import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from "react";
import { getAllClassNames } from "helpers";
import styles from "./CustomDropdownCheckboxes.module.scss";
import Text from "components/text/Text";
import { Arrow, Close } from "assets/icons";
import { useClickOutside } from "hooks";
import strings from "assets/strings";

export type ItemProps = {
  value: string;
  label: string;
  isSelected: boolean;
};

type IDropdownProps = {
  onSelected: (subbubbles: ItemProps[]) => void;
  selectOptions: ItemProps[];
  selectedCheckBoxes: string[];
  title?: string;
  setQueryParams: (items: string[]) => void;
}

export const CustomDropdownCheckboxes = forwardRef((
  {
    onSelected,
    selectOptions,
    selectedCheckBoxes,
    title,
    setQueryParams
  }: IDropdownProps,
  ref // Forwarded ref to expose methods to parent
) => {

  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCheckboxes, setSelectedCheckboxes] = useState(selectedCheckBoxes ? selectedCheckBoxes : [strings.searchDoctors.allSubMedicalFields] as string[]);
  const [focusedIndex, setFocusedIndex] = useState(-1); // New state for managing focus index
  const dropdownRef = useRef<HTMLDivElement>(null);
  const arrowRef = useRef<SVGSVGElement>(null);

  useClickOutside(dropdownRef, () => setShowDropdown(false));

  // Expose the arrow focus functionality to the parent
  useImperativeHandle(ref, () => ({
    focusArrow: () => {
      if (arrowRef.current) {
        arrowRef.current.focus(); // Ensure `focus()` works on the SVG element  
      } 
    },
  }));

  const isSelected = (value: string): boolean => selectedCheckboxes.includes(value);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showDropdown) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showDropdown]);

  const handleCloseKeyDown = (event: React.KeyboardEvent<SVGSVGElement>, item: string) => {
    if (event.key === "Enter" || event.key === " ") {
      handleSelectedOption(item);
    }
  };

  const handleArrowKeyDown = (event: React.KeyboardEvent<SVGSVGElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setShowDropdown((prevState) => !prevState);
    }
  };

  useEffect(() => {
    setSelectedCheckboxes(selectedCheckBoxes);
  }, [selectedCheckBoxes]);

  useEffect(() => {
    if (focusedIndex !== -1 && showDropdown) {
      const focusedElement = document.querySelector(`.dropdown-item-${focusedIndex}`) as HTMLElement;
      if (focusedElement) {
        focusedElement.focus();
      }
    }
  }, [focusedIndex]);

  const handleSelectedOption = (value: string) => {
    let temp = [...selectedCheckboxes];
    if (value !== strings.searchDoctors.allSubMedicalFields) {
      temp = temp.filter((item) => item !== strings.searchDoctors.allSubMedicalFields);
    }
    if (value === strings.searchDoctors.allSubMedicalFields) {
      temp = temp.filter((item) => item === strings.searchDoctors.allSubMedicalFields);
    }
    const index = temp.findIndex((val) => val === value);
    if (index !== -1) temp.splice(index, 1);
    else temp.push(value);
    setSelectedCheckboxes(temp);
    setQueryParams(temp);
    const selectedSubBubbles = temp.map((item) => ({ value: item, label: item, isSelected: true }));
    onSelected(selectedSubBubbles);
  };

  const handleDropdownKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const maxIndex = selectOptions.length - 1;
    if (event.key === "ArrowDown") {
      setFocusedIndex((prevIndex) => Math.min(prevIndex + 1, maxIndex));
    }
    if (event.key === "ArrowUp") {
      setFocusedIndex((prevIndex) => Math.max(prevIndex - 1, 0));
    }
    if (event.key === "Enter" || event.key === " ") {
      handleSelectedOption(selectOptions[focusedIndex].label);
    }
  };

  return (
    <div className={styles.container} ref={dropdownRef}>
      <div className={styles.divTop}>
        {title && <Text className={styles.title} text={title} />}
      </div>
      <div
        className={getAllClassNames([
          styles.dropdownContainer,
        ])}
      >
        {selectedCheckboxes.map((item) => (
          <div
            key={item}
            className={getAllClassNames([
              styles.bubble,
              "ais-RefinementList-item",
            ])}
          >
            <span
              className={getAllClassNames([
                "ais-RefinementList-labelText",
                styles.label,
              ])}
            >
              {item}
            </span>
            <Close
              onClick={() => {
                handleSelectedOption(item);
              }}
              className={styles.icon}
              aria-label={`${strings.globalStrings.remove} ${item}`} // Added aria-label for accessibility
              role="button" // Added role for screen readers
              tabIndex={0} // Make the element focusable
              onKeyDown={(event) => handleCloseKeyDown(event, item)}
            />
          </div>
        ))}
        <Arrow
          ref={arrowRef}
          className={styles.iconArrow}
          onClick={() => { setShowDropdown(!showDropdown); }}
          aria-label={`${showDropdown ? strings.globalStrings.collapse : strings.globalStrings.expand} ${title}`} // Added aria-label for accessibility
          role="button" // Added role for screen readers
          tabIndex={0} // Make the element focusable
          onKeyDown={handleArrowKeyDown}
        />
      </div>
      {showDropdown && (
        <div className={styles.dropdown} onKeyDown={handleDropdownKeyDown}>
          {selectOptions.map((item, index) => (
            <div
              key={item.value}
              className={`dropdown-item-${index} ${getAllClassNames([
                "ais-RefinementList-item",
                isSelected(item.label) && "ais-RefinementList-item--selected",
              ])}`}
              tabIndex={0} // Make the element focusable
            >
              <label>
                <input
                tabIndex={-1} // Remove the input from the tab order
                  type="checkbox"
                  value={item.value}
                  checked={isSelected(item.label)}
                  className={styles.checkbox}
                  onChange={() => {
                    handleSelectedOption(item.label);
                  }}
                />
                {item.label}
              </label>
            </div>
          ))}
          <div className={styles.height} />
        </div>
      )}
    </div>
  );
});